import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../providers/authprovider";
import handleResponseCode from "./utils/handleResponseCode";
import { Alert, Button, Spinner, Table, Badge, Modal } from "react-bootstrap";
import useInvoiceModal from "./hooks/useInvoiceModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import RecurringInvoices from "./RecurringInvoices";

export default function InvoicesToIssue({ onInvoiceAdded }) {
  const navigate = useNavigate();
  const { invoices: invoicesApi } = useAuth();

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal faktury
  const { createInvoiceModal, CreateInvoiceModal } = useInvoiceModal();

  // modal potwierdzenia usunięcia
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, building_name, created_at, description } itd.
  const [deleting, setDeleting] = useState(false);

  const openDeleteModal = (inv) => {
    setError("");
    setInfo("");
    setDeleteTarget(inv);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError("");
    setInfo("");

    const result = await invoicesApi.fetchInvoicesToIssue();

    if (!result?.success) {
      setInvoices([]);
      setError(handleResponseCode(result?.status, result?.error));
      setLoading(false);
      return;
    }

    setInvoices(result.content || []);
    setLoading(false);
  }, [invoicesApi]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setError("");
    setInfo("");

    const result = await invoicesApi.deleteInvoiceToIssue(deleteTarget.id);

    if (!result?.success) {
      setError(handleResponseCode(result?.status, result?.error));
      setDeleting(false);

      closeDeleteModal();
      return;
    }

    // usuń z listy lokalnie
    setInvoices((old) => old.filter((i) => i.id !== deleteTarget.id));
    setInfo("Usunięto pozycję z listy faktur do wystawienia.");
    setDeleting(false);
    closeDeleteModal();
  }, [deleteTarget, invoicesApi]);

  const handleDownloadToIssueFile = useCallback(
    async ({ invoiceToIssueId, type, fileName }) => {
      if (!invoiceToIssueId || !type) return;

      setError("");

      const result = await invoicesApi.downloadInvoiceToIssuePdf({
        invoiceToIssueId,
        type,
        fileName,
      });

      if (!result?.success) {
        setError(handleResponseCode(result?.status, result?.error));
      }
    },
    [invoicesApi],
  );

  const deleteSummary = useMemo(() => {
    if (!deleteTarget) return null;
    return {
      building:
        deleteTarget.building_name || deleteTarget?.building?.name || "-",
      createdAt: formatDateTime(deleteTarget.created_at),
      description: deleteTarget.description || "-",
    };
  }, [deleteTarget]);

  return (
    <div className="container py-3">
      {/* Nagłówek pokazujemy po załadowaniu */}
      {!loading ? (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1">
              Faktury do wystawienia{" "}
              <Badge bg="secondary">{invoices.length}</Badge>
            </h4>
          </div>

          <div>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Wróć
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {info ? <Alert variant="success" onClose={() => setInfo(null)} dismissible>{info}</Alert> : null}

      {loading ? (
        <div className="py-3">
          <Spinner size="sm" className="me-2" />
          Ładowanie...
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>L.p.</th>
              <th>Obiekt</th>
              <th>Utworzono</th>
              <th>Opis</th>
              <th>Zlecenie</th>
              <th>Inne</th>
              <th>Protokół</th>
              <th>Faktura</th>
              <td>Usuń</td>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  Brak faktur do wystawienia.
                </td>
              </tr>
            ) : (
              invoices.map((inv, index) => (
                <tr key={inv.id}>
                  <td>{index + 1}</td>
                  <td>{inv.building_name}</td>
                  <td>{formatDateTime(inv.created_at)}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{inv.description}</td>
                  <td>
                    {inv.file_order_name ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownloadToIssueFile({
                            invoiceToIssueId: inv.id,
                            type: "zlecenie",
                            fileName: inv.file_order_name,
                          });
                        }}
                      >
                        {inv.file_order_name}
                      </a>
                    ) : null}
                  </td>
                  <td>
                    {inv.file_different_name ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDownloadToIssueFile({
                            invoiceToIssueId: inv.id,
                            type: "inne",
                            fileName: inv.file_different_name,
                          });
                        }}
                      >
                        {inv.file_different_name}
                      </a>
                    ) : null}
                  </td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate(`/protocols/${inv.building.id}`)}
                    >
                      Protokół
                    </Button>
                  </td>
                  <td>
                    <Button
                      onClick={() => {
                        createInvoiceModal.show({
                          title: "Nowa faktura",
                          initialBuilding: inv.building,
                          initialDescription: "",
                          invoiceToIssueId: inv.id,
                          onSaved: (invoice, building) => {
                            setInvoices((oldInvoices) =>
                              oldInvoices.filter((i) => i.id !== inv.id),
                            );
                            onInvoiceAdded?.(invoice, building);
                          },
                        });
                      }}
                    >
                      Faktura
                    </Button>
                  </td>
                  <td>
                    <Button
                      variant="outline-danger"
                      onClick={() => openDeleteModal(inv)}
                      disabled={deleting && deleteTarget?.id === inv.id}
                      title="Usuń"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <RecurringInvoices onInvoiceAdded={onInvoiceAdded} />

      {/* MODAL: potwierdzenie usunięcia */}
      <Modal show={deleteModalOpen} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Potwierdź usunięcie</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteTarget ? (
            <>
              <div className="mb-2">
                Czy na pewno chcesz usunąć pozycję z listy „Faktury do
                wystawienia”?
              </div>

              <div className="border rounded p-2">
                <div>
                  <strong>Obiekt:</strong> {deleteSummary?.building}
                </div>
                <div>
                  <strong>Utworzono:</strong> {deleteSummary?.createdAt}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  <strong>Opis:</strong> {deleteSummary?.description}
                </div>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                Tej operacji nie można cofnąć.
              </div>
            </>
          ) : (
            <div className="text-muted">Brak danych do usunięcia.</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDeleteModal}
            disabled={deleting}
          >
            Anuluj
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={!deleteTarget?.id || deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Usuwanie...
              </>
            ) : (
              "Usuń"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CreateInvoiceModal />
    </div>
  );
}

const formatDateTime = (iso) => {
  if (!iso) return "";

  return new Intl.DateTimeFormat("pl-PL", {
    timeZone: "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(iso));
};
